/**
 * Determines if the input is a pojo.
 *
 * @param value The value to test
 * @returns If the input is a pojo
 */
const isObject = (value) => typeof value === 'object' && !Array.isArray(value) && value !== null;
/**
 * Remaps an object keys and values based on the callback function.
 *
 * @see https://stackoverflow.com/a/14810722
 *
 * @param obj An object to remap
 * @param callbackFn The mapping function
 * @returns The mapped object
 */
function transmuteObject(obj, callbackFn) {
    const entries = Object.entries(obj);
    const mapEntries = entries.map(([key, value]) => callbackFn(key, isObject(value) ? transmuteObject(value, callbackFn) : value));
    return Object.fromEntries(mapEntries);
}
/**
 * Recursively invokes a callback fn for each nested object reference.
 *
 * @param obj The object to recurse
 * @param callbackFn The function to call back
 */
function recurseObject(obj, callbackFn) {
    callbackFn(obj);
    for (const key in obj) {
        const value = obj[key];
        if (isObject(value)) {
            recurseObject(value, callbackFn);
        }
    }
}
/**
 * Processes a theme options object and applies known fixes necessary as part of our Figma export
 * process.
 *
 * Initial context: https://github.com/MontuGroup/montu-web/pull/415/files
 *
 * @param themeObject Incoming brand theme configuration
 * @returns The theme options with manual fix replacements
 */
export const fixThemeOptions = (themeObject, options) => {
    const cssVarPrefix = themeObject.cssVarPrefix;
    const mappedTheme = transmuteObject(themeObject, (key, value) => {
        // Replace "var(--mui-" with "var(--<cssVarPrefix>-"
        if (cssVarPrefix && typeof value === 'string' && value.includes('var(--mui-')) {
            value = value.replace(/var\(--mui-/g, `var(--${cssVarPrefix}-`);
        }
        // https://github.com/mui/mui-design-kits/issues/378
        if (key === 'contrast') {
            key = 'contrastText';
        }
        if (typeof value === 'string' && value.includes('-contrast)')) {
            value = value.replace('-contrast)', '-contrastText)');
        }
        // We had some Figma local variables for typography incorrectly modified
        if (key === 'subtitle 1') {
            key = 'subtitle1';
        }
        if (key === 'subtitle 2') {
            key = 'subtitle2';
        }
        if (key === 'body 1') {
            key = 'body1';
        }
        if (key === 'body 2') {
            key = 'body2';
        }
        if (key === 'main 2') {
            key = 'main';
        }
        return [key, value];
    });
    // Scrub out unexpected hidden elements https://github.com/mui/mui-design-kits/issues/362
    recurseObject(mappedTheme, (nestedObj) => {
        if ('display' in nestedObj && nestedObj['display'] === 'none') {
            delete nestedObj['display'];
            if ('color' in nestedObj && typeof nestedObj['color'] === 'string' && nestedObj['color'].includes('-disabled')) {
                delete nestedObj['color'];
            }
        }
    });
    if (options?.disableDarkMode) {
        mappedTheme.colorSchemes.dark = false;
    }
    return mappedTheme;
};
//# sourceMappingURL=data:application/json;base64,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